import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Root, Portal, Overlay, Content, Title, Close } from '@radix-ui/react-dialog';

import { getAuth, putAuth } from '@/api/ApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { NedarimMosadSecretsDtoIn, UpdateNedarimMosadSecretsDtoOut } from '@/models/in/NedarimMosadCredentialDtoIn';
import closeIcon from '@/assets/icons/close.svg';
import styles from './SecretDialog.module.css';

type SecretField = 'apiPassword' | 'apiValid';

interface Props {
  mosadId: number;
  mosadName: string | null;
  field: SecretField;
  /** Whether the store holds this secret at all; when false there is nothing to reveal. */
  has: boolean;
  label: string;
  /** Refreshes the table after a save, so the ownership and status cells stay truthful. */
  onSaved?: () => void;
}

const QUERY_URL = 'management/nedarim-credentials';

const CopyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path d="M5 15V5a2 2 0 0 1 2-2h10" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

/**
 * Opens a centered dialog holding a single secret, an icon-only copy button, and - for an
 * admin - a field to replace it.
 *
 * Each field reveals on its own, so opening the password never exposes ApiValid, and each
 * saves on its own too: the endpoint leaves a blank field untouched, which is what keeps an
 * update aimed at the password from blanking an ApiValid that cannot be regenerated.
 *
 * The mosad is opened for editing even with nothing stored, because that is the case that
 * matters most - the individually-configured mosadot arrive here with an empty row, and
 * entering their key is how they stop depending on an environment variable.
 */
const SecretDialog = ({ mosadId, mosadName, field, has, label, onSaved }: Props) => {
  const { t } = useTranslation();
  const { currentUser, isCurrentUserAdmin } = useAuthContext();
  const canEdit = isCurrentUserAdmin();

  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  // Nothing stored and no permission to enter anything: the cell has nothing to offer.
  if (!has && !canEdit) return <span className="text-(--font-body-3)">—</span>;

  const authHeader = { Authorization: `Bearer ${currentUser?.token}` };

  const load = async () => {
    if (loaded || loading) return;
    setLoading(true);
    try {
      // The endpoint returns both secrets, but the dialog shows only this field -
      // opening one never displays the other.
      const result = await getAuth<NedarimMosadSecretsDtoIn>(`${QUERY_URL}/${mosadId}/secrets`, authHeader);
      setValue(result[field]);
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  const onOpenChange = async (next: boolean) => {
    setOpen(next);
    if (!next) {
      setCopied(false);
      setDraft('');
      setSaved(false);
      setSaveFailed(false);
      return;
    }
    if (has) await load();
  };

  const copy = async () => {
    if (!value) return;
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const save = async () => {
    const next = draft.trim();
    if (!next) return;

    setSaving(true);
    setSaved(false);
    setSaveFailed(false);
    try {
      await putAuth<unknown, object, UpdateNedarimMosadSecretsDtoOut>(
        `${QUERY_URL}/${mosadId}/secrets`,
        authHeader,
        {},
        {
          [field]: next,
        }
      );
      setValue(next);
      setLoaded(true);
      setDraft('');
      setSaved(true);
      onSaved?.();
    } catch {
      setSaveFailed(true);
    } finally {
      setSaving(false);
    }
  };

  const subtitle = [`${t('nedarimCredentials.columns.mosadId')} ${mosadId}`, mosadName].filter(Boolean).join(' · ');

  return (
    <Root open={open} onOpenChange={onOpenChange}>
      <button className="body-md-link text-(--font-link)" onClick={() => onOpenChange(true)}>
        {has ? t('nedarimCredentials.reveal') : t('nedarimCredentials.edit')}
      </button>

      <Portal>
        <Overlay className={styles.overlay} />
        <Content className={styles.content} aria-describedby={undefined}>
          <Close className={styles.closeButton} aria-label={t('close')}>
            <img src={closeIcon} alt="" />
          </Close>

          <div className={styles.header}>
            <Title className={styles.title}>{label}</Title>
            <p className={styles.subtitle}>{subtitle}</p>
          </div>

          {has &&
            (loading ? (
              <p className={styles.state}>{t('nedarimCredentials.revealing')}</p>
            ) : value ? (
              <div className={styles.valueRow}>
                <span className={styles.value}>{value}</span>
                <button
                  className={`${styles.copyButton} ${copied ? styles.copied : ''}`}
                  onClick={copy}
                  title={t('nedarimCredentials.copy')}
                  aria-label={t('nedarimCredentials.copy')}
                >
                  {copied ? <CheckIcon /> : <CopyIcon />}
                </button>
              </div>
            ) : (
              <p className={styles.stateError}>{t('nedarimCredentials.undecryptable')}</p>
            ))}

          {canEdit && (
            <div className={styles.editSection}>
              <label className={styles.editLabel} htmlFor={`secret-${mosadId}-${field}`}>
                {t('nedarimCredentials.newValue')}
              </label>
              <input
                id={`secret-${mosadId}-${field}`}
                className={styles.editInput}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={t('nedarimCredentials.newValuePlaceholder')}
                autoComplete="off"
                spellCheck={false}
              />
              <p className={styles.notice}>{t('nedarimCredentials.manualNotice')}</p>
              <div className={styles.editActions}>
                <button className={styles.saveButton} onClick={save} disabled={saving || !draft.trim()}>
                  {saving ? t('nedarimCredentials.saving') : t('nedarimCredentials.save')}
                </button>
                {saved && <span className={styles.savedNote}>{t('nedarimCredentials.saved')}</span>}
                {saveFailed && <span className={styles.errorNote}>{t('nedarimCredentials.saveError')}</span>}
              </div>
            </div>
          )}
        </Content>
      </Portal>
    </Root>
  );
};

export default SecretDialog;
